import {
  Alert,
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  Card,
  Form,
  Grid,
  newToast,
  Page,
  PageRoute,
  pn,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useParams,
  useTheme,
  useValidationErrors,
} from "@compass/core";
import { Input, SubmitButton } from "components";
import {
  ForgotCredential,
  ForgotCredentialType,
  ForgotCredentialViewResource,
  ModelPropertyBase,
} from "generated/openApi";
import produce from "immer";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import ApiService from "services/apiService";
import { loginPageRoute } from "../LoginPage/LoginPage";

const routeName = "forgotCredential";

class OptionalParams {
  tenantName?: string = "";
}

export const forgotCredentialPageRoute: PageRoute<undefined, OptionalParams> = {
  handle: "Forgot Credentials",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
  getOptionalParams: (params) => buildOptionalParams(params),
};

export const ForgotCredentialPage = () => {
  const { optionalParams } = useParams(routeName, undefined, OptionalParams);

  const theme = useTheme();
  const dispatch = useDispatch();

  const [forgotCredential, setForgotCredential] = useState<ForgotCredential>({
    tenantName: optionalParams.tenantName,
    forgotCredentialType: ForgotCredentialType.ForgotPassword,
  } as ForgotCredential);

  const { validationErrors, setValidationErrors } = useValidationErrors();
  const navigate = useNavigate();

  const [viewResource, setViewResource] = useState<ForgotCredentialViewResource>({});

  useEffect(() => {
    fetchViewResource();
  }, []);

  const fetchViewResource = () => {
    ApiService.auth.getForgotCredentialViewResource().then((response) => {
      setViewResource(response);
    });
  };

  const post = () => {
    setValidationErrors({});

    ApiService.auth
      .postForgotCredential(forgotCredential)
      .then(() => {
        dispatch(setAppToast(newToast("success")));
        navigate({
          pathname: `/${loginPageRoute.getPath()}`,
        });
      })
      .catch((ex) => {
        dispatch(setAppToast(newToast("error")));
        setValidationErrors(ex.response);
      });
  };

  const properties = viewResource.properties || [];

  const forgotCredentialTypeField = () => {
    return (
      <ToggleButtonGroup
        fullWidth
        value={forgotCredential.forgotCredentialType}
        exclusive
        onChange={(event: React.MouseEvent<HTMLElement>, value: ForgotCredentialType | null) => {
          setForgotCredential(
            produce((draft) => {
              draft.forgotCredentialType = value!;
            })
          );
        }}
      >
        <ToggleButton value={ForgotCredentialType.ForgotPassword}>I Forgot My Password</ToggleButton>
        <ToggleButton value={ForgotCredentialType.ForgotUsername}>I Forgot My Username</ToggleButton>
      </ToggleButtonGroup>
    );
  };

  const tenantNameField = () => {
    const propertyName = pn<ForgotCredential>("tenantName");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Input
        modelProperty={property}
        errors={validationErrors?.[propertyName]}
        value={forgotCredential?.tenantName}
        onChange={(e) =>
          setForgotCredential(
            produce((draft) => {
              draft.tenantName = e.target.value;
            })
          )
        }
        hideCount
      />
    );
  };

  const emailField = () => {
    const propertyName = pn<ForgotCredential>("email");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Input
        modelProperty={property}
        errors={validationErrors?.[propertyName]}
        value={forgotCredential?.email}
        onChange={(e) =>
          setForgotCredential(
            produce((draft) => {
              draft.email = e.target.value;
            })
          )
        }
        hideCount
      />
    );
  };

  return (
    <Page>
      <Stack alignItems="center" justifyContent="center" height="100%">
        <Card sx={{ maxWidth: "550px", width: "100%" }}>
          <Stack px={3} py={7} spacing="lg">
            <Stack spacing="sm" alignItems="center">
              <Typography variant="h4" fontWeight={theme.typography.fontWeightMedium}>
                Forgot Username/Password
              </Typography>

              <Typography variant="subtitle2">
                Forgot your username or password? Please enter your email. An email with instruction will be sent to
                you.
              </Typography>
            </Stack>

            <Form>
              <Stack spacing="md">
                {validationErrors?.[pn<ForgotCredential>("generalValidationErrors")] && (
                  <Alert severity="error">{validationErrors?.[pn<ForgotCredential>("generalValidationErrors")]?.[0]}</Alert>
                )}

                {forgotCredentialTypeField()}

                {tenantNameField()}

                {emailField()}

                <SubmitButton onClick={post} label="Send Me Email" fullWidth sx={{ height: "50px" }} />
              </Stack>
            </Form>
          </Stack>
        </Card>
      </Stack>
    </Page>
  );
};
